// src/pages/b2b/B2BSettingsPage.js - B2B 설정 페이지

import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import B2BLayout from '../../components/b2b/B2BLayout';
import { ConfirmModal, Toast } from '../../components/ui';
import { useAppContext } from '../../context/AppContext';
import { useB2BMode } from '../../hooks/useB2BMode';
import {
  createEnterpriseUser,
  deleteAccount,
  enterpriseChangePassword,
  getB2BMembershipUsage,
  getMyTeam,
  removeTeamMember,
  resetTeamMemberPassword,
  updateEnterpriseProfile,
  updateTeamMemberProfile,
} from '../../services/api';
import './B2BSettingsPage.scss';

const SECTION_LABELS = {
  account: '계정설정',
  figma: '피그마 연동',
  members: '멤버 관리',
  membership: '멤버십 관리',
};

const getMemberId = (member) => member?.user_id || member?.id || member?.user?.id;

const getMemberRole = (member) => {
  if (member?.role) return member.role;
  if (member?.company_role) return member.company_role;
  if (member?.is_owner) return 'owner';
  return 'member';
};

const getMemberName = (member) => member?.name || member?.user?.name || '';

const getMemberEmail = (member) => member?.email || member?.user?.email || '';

const getMemberDepartment = (member) => member?.department || '-';

const getMemberLlmUsage = (member) => {
  const llmUsage = member?.llm_usage || {};
  const legacyUsage = member?.usage || {};
  return {
    weightedTokens: llmUsage.billable_weighted_tokens ?? legacyUsage.billable_weighted_tokens,
    lastUsedAt: llmUsage.last_used_at || legacyUsage.last_used_at,
  };
};

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getLastDaysRange = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  return {
    startDate: formatDateInputValue(start),
    endDate: formatDateInputValue(end),
  };
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const formatTokenCount = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return Number(value || 0).toLocaleString('ko-KR');
};

const formatChartTooltipDate = (value) => {
  if (!value) return '-';
  const normalized = String(value).length === 7 ? `${value}-01` : value;
  const date = new Date(`${normalized}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatMemberDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const B2BSettingsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loginUser, logoutUser, isEnterprise } = useAppContext();
  const { disableB2BMode } = useB2BMode();
  const section = new URLSearchParams(location.search).get('section') || 'account';
  const sectionLabel = SECTION_LABELS[section] || SECTION_LABELS.account;
  const [profileForm, setProfileForm] = useState({
    name: '',
    department: '',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [toast, setToast] = useState({ isOpen: false, type: 'success', message: '' });
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordFieldErrors, setPasswordFieldErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState('');
  const [teamLoaded, setTeamLoaded] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberSort, setMemberSort] = useState('created_desc');
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [addMemberForm, setAddMemberForm] = useState({ email: '', name: '', department: '' });
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [addMemberFieldErrors, setAddMemberFieldErrors] = useState({ email: '', name: '', department: '' });
  const [createdMemberResult, setCreatedMemberResult] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [editMemberForm, setEditMemberForm] = useState({ name: '', department: '', role: 'member' });
  const [editMemberLoading, setEditMemberLoading] = useState(false);
  const [editMemberError, setEditMemberError] = useState('');
  const [editConfirmOpen, setEditConfirmOpen] = useState(false);
  const [resetMemberLoading, setResetMemberLoading] = useState(false);
  const [resetMemberError, setResetMemberError] = useState('');
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [deletingMember, setDeletingMember] = useState(null);
  const [deleteMemberLoading, setDeleteMemberLoading] = useState(false);
  const [deleteMemberError, setDeleteMemberError] = useState('');
  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [membershipUsage, setMembershipUsage] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipError, setMembershipError] = useState('');
  const [membershipRange, setMembershipRange] = useState('30');
  const [membershipStartDate, setMembershipStartDate] = useState(() => getLastDaysRange(30).startDate);
  const [membershipEndDate, setMembershipEndDate] = useState(() => getLastDaysRange(30).endDate);

  useEffect(() => {
    setProfileForm({
      name: user?.name || '',
      department: user?.department || '',
    });
  }, [user]);

  const handleProfileChange = (field, value) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
    setProfileMessage('');
  };

  const hasProfileChanges =
    profileForm.name !== (user?.name || '') ||
    profileForm.department !== (user?.department || '');
  const isPasswordFormComplete = Boolean(
    passwordForm.currentPassword &&
    passwordForm.newPassword &&
    passwordForm.confirmPassword
  );
  const isAddMemberFormComplete = Boolean(
    addMemberForm.email.trim() &&
    addMemberForm.name.trim() &&
    addMemberForm.department.trim()
  );

  const showToast = (message, type = 'success') => {
    setToast({ isOpen: true, type, message });
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!profileForm.name.trim()) {
      setProfileMessage('이름을 입력해주세요.');
      return;
    }
    try {
      setProfileLoading(true);
      setProfileMessage('');
      const data = await updateEnterpriseProfile({
        name: profileForm.name.trim(),
        department: profileForm.department.trim(),
      });
      if (data.user) {
        loginUser(data.user, data.access_token || null);
      }
      setProfileMessage('');
      showToast('정보가 저장되었습니다.');
    } catch (err) {
      console.error('프로필 수정 실패:', err);
      setProfileMessage(err.message || '프로필 수정에 실패했습니다.');
    } finally {
      setProfileLoading(false);
    }
  };

  const resetPasswordModal = () => {
    setPasswordModalOpen(false);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordFieldErrors({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordLoading(false);
  };

  const handlePasswordFormChange = (field, value) => {
    const nextForm = { ...passwordForm, [field]: value };
    setPasswordForm(nextForm);
    setPasswordFieldErrors((prev) => ({
      ...prev,
      [field]: '',
      confirmPassword:
        nextForm.newPassword && nextForm.confirmPassword && nextForm.newPassword !== nextForm.confirmPassword
          ? '비밀번호가 일치하지 않습니다.'
          : '',
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    };

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      if (!passwordForm.currentPassword) nextErrors.currentPassword = '현재 비밀번호를 입력해 주세요.';
      if (!passwordForm.newPassword) nextErrors.newPassword = '새 비밀번호를 입력해 주세요.';
      if (!passwordForm.confirmPassword) nextErrors.confirmPassword = '비밀번호 확인을 입력해 주세요.';
      setPasswordFieldErrors(nextErrors);
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordFieldErrors((prev) => ({
        ...prev,
        confirmPassword: '비밀번호가 일치하지 않습니다.',
      }));
      return;
    }

    try {
      setPasswordLoading(true);
      const data = await enterpriseChangePassword(passwordForm.currentPassword, passwordForm.newPassword);
      if (data.user) {
        loginUser(data.user, null);
      }
      resetPasswordModal();
      showToast('비밀번호 변경이 완료되었습니다.');
    } catch (err) {
      console.error('비밀번호 변경 실패:', err);
      const message = err.message || '비밀번호 변경에 실패했습니다.';
      const normalizedMessage = message.toLowerCase();
      if (
        message.includes('현재 비밀번호') ||
        normalizedMessage.includes('current password') ||
        normalizedMessage.includes('invalid password') ||
        normalizedMessage.includes('incorrect password')
      ) {
        setPasswordFieldErrors((prev) => ({
          ...prev,
          currentPassword: '현재 비밀번호를 다시 확인해 주세요.',
        }));
      } else if (message.includes('비밀번호') || message.includes('password')) {
        setPasswordFieldErrors((prev) => ({
          ...prev,
          newPassword: message,
        }));
      } else {
        showToast(message, 'error');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const loadMembers = useCallback(async () => {
    setMembersLoading(true);
    setTeamLoaded(false);
    setMembersError('');
    try {
      const data = await getMyTeam();
      if (data.success === false) {
        setMembersError(data.error || '멤버 정보를 불러오는데 실패했습니다.');
        return;
      }
      setCompany(data.company || data.team || null);
      setMembers(data.members || []);
    } catch (err) {
      console.error('멤버 목록 조회 실패:', err);
      setMembersError(err.message || '멤버 정보를 불러오는데 실패했습니다.');
    } finally {
      setMembersLoading(false);
      setTeamLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (section === 'members' || section === 'membership') {
      loadMembers();
    }
  }, [loadMembers, section]);

  useEffect(() => {
    const days = membershipRange === '180' ? 180 : membershipRange === '90' ? 90 : 30;
    const range = getLastDaysRange(days);
    setMembershipStartDate(range.startDate);
    setMembershipEndDate(range.endDate);
  }, [membershipRange]);

  const currentMember = members.find((member) => String(getMemberId(member)) === String(user?.id));
  const isOwner = (
    getMemberRole(currentMember) === 'owner' ||
    user?.company_role === 'owner' ||
    user?.role === 'owner'
  );
  const isCurrentUserMember = (member) => {
    const memberId = getMemberId(member);
    const memberEmail = getMemberEmail(member).toLowerCase();
    const userEmail = String(user?.email || '').toLowerCase();
    return (
      (memberId && user?.id && String(memberId) === String(user.id)) ||
      (memberEmail && userEmail && memberEmail === userEmail)
    );
  };

  const loadMembershipUsage = useCallback(async () => {
    setMembershipLoading(true);
    setMembershipError('');
    try {
      const data = await getB2BMembershipUsage({
        period: 'daily',
        start_date: membershipStartDate,
        end_date: membershipEndDate,
      });
      if (data.success === false) {
        setMembershipError(data.error || '멤버십 사용량을 불러오는데 실패했습니다.');
        return;
      }
      setMembershipUsage(data);
      setCompany((prev) => data.company || data.team || prev);
      if (Array.isArray(data.members)) setMembers(data.members);
    } catch (err) {
      console.error('멤버십 사용량 조회 실패:', err);
      setMembershipError(err.message || '멤버십 사용량 API 연동이 필요합니다.');
      if (members.length === 0) {
        await loadMembers();
      }
    } finally {
      setMembershipLoading(false);
    }
  }, [loadMembers, members.length, membershipEndDate, membershipStartDate]);

  useEffect(() => {
    if (section === 'membership' && teamLoaded && !membersLoading && isOwner) {
      loadMembershipUsage();
    }
  }, [isOwner, loadMembershipUsage, membersLoading, section, teamLoaded]);

  useEffect(() => {
    if (['members', 'membership'].includes(section) && teamLoaded && !membersLoading && !isOwner) {
      navigate('/b2b/settings?section=account', { replace: true });
    }
  }, [isOwner, membersLoading, navigate, section, teamLoaded]);

  const getMemberCreatedAt = (member) => member?.created_at || member?.joined_at || member?.user?.created_at;

  const filteredMembers = members
    .filter((member) => {
      const query = memberSearch.trim().toLowerCase();
      if (!query) return true;
      return [
        getMemberEmail(member),
        getMemberName(member),
        getMemberDepartment(member),
      ].some((value) => String(value || '').toLowerCase().includes(query));
    })
    .sort((a, b) => {
      if (memberSort === 'name_asc') {
        return getMemberName(a).localeCompare(getMemberName(b), 'ko');
      }
      const aTime = new Date(getMemberCreatedAt(a) || 0).getTime();
      const bTime = new Date(getMemberCreatedAt(b) || 0).getTime();
      return memberSort === 'created_asc' ? aTime - bTime : bTime - aTime;
    });

  const closeAddMemberModal = () => {
    if (addMemberLoading) return;
    setAddMemberModalOpen(false);
    setAddMemberForm({ email: '', name: '', department: '' });
    setAddMemberFieldErrors({ email: '', name: '', department: '' });
  };

  const handleAddMemberFormChange = (field, value) => {
    setAddMemberForm((prev) => ({ ...prev, [field]: value }));
    setAddMemberFieldErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    const email = addMemberForm.email.trim();
    const name = addMemberForm.name.trim();
    const department = addMemberForm.department.trim();
    const nextErrors = { email: '', name: '', department: '' };

    if (!email) nextErrors.email = '회사 이메일 주소를 입력해 주세요.';
    else if (!isValidEmail(email)) nextErrors.email = '올바른 이메일 형식이 아닙니다.';
    else if (members.some((member) => getMemberEmail(member).toLowerCase() === email.toLowerCase())) {
      nextErrors.email = '이미 등록된 이메일입니다.';
    }
    if (!name) nextErrors.name = '이름을 입력해 주세요.';
    if (!department) nextErrors.department = '소속 부서명을 입력해 주세요.';

    if (Object.values(nextErrors).some(Boolean)) {
      setAddMemberFieldErrors(nextErrors);
      return;
    }
    const companyId = company?.id || user?.company_id;
    if (!companyId) {
      showToast('회사 정보를 확인할 수 없습니다.', 'error');
      return;
    }

    setAddMemberLoading(true);
    setAddMemberFieldErrors({ email: '', name: '', department: '' });
    try {
      const data = await createEnterpriseUser({
        email,
        name,
        company_id: Number(companyId),
        department,
        role: 'member',
      });
      if (data.success === false) {
        const message = data.error || '계정 추가에 실패했습니다.';
        const normalizedMessage = message.toLowerCase();
        setAddMemberFieldErrors((prev) => ({
          ...prev,
          email: message.includes('등록') ||
            message.includes('이미') ||
            normalizedMessage.includes('duplicate') ||
            normalizedMessage.includes('already') ||
            normalizedMessage.includes('exist')
            ? '이미 등록된 이메일입니다.'
            : message,
        }));
        return;
      }
      setCreatedMemberResult({
        email,
        password: data.temporary_password || data.temp_password || data.initial_password || '0000',
      });
      setAddMemberModalOpen(false);
      setAddMemberForm({ email: '', name: '', department: '' });
      setAddMemberFieldErrors({ email: '', name: '', department: '' });
      await loadMembers();
    } catch (err) {
      console.error('계정 추가 실패:', err);
      const message = err.message || '계정 추가에 실패했습니다.';
      const normalizedMessage = message.toLowerCase();
      setAddMemberFieldErrors((prev) => ({
        ...prev,
        email: message.includes('등록') ||
          message.includes('이미') ||
          normalizedMessage.includes('duplicate') ||
          normalizedMessage.includes('already') ||
          normalizedMessage.includes('exist')
          ? '이미 등록된 이메일입니다.'
          : message,
      }));
    } finally {
      setAddMemberLoading(false);
    }
  };

  const openEditMemberModal = (member) => {
    if (isCurrentUserMember(member)) return;
    setEditingMember(member);
    setEditMemberForm({
      name: getMemberName(member),
      department: member?.department || '',
      role: getMemberRole(member),
    });
    setEditMemberError('');
    setResetMemberError('');
    setResetPasswordResult(null);
  };

  const closeEditMemberModal = () => {
    if (editMemberLoading || resetMemberLoading) return;
    setEditingMember(null);
    setEditMemberForm({ name: '', department: '', role: 'member' });
    setEditMemberError('');
    setEditConfirmOpen(false);
    setResetMemberError('');
    setResetPasswordResult(null);
  };

  const handleEditMemberFormChange = (field, value) => {
    setEditMemberForm((prev) => ({ ...prev, [field]: value }));
    setEditMemberError('');
  };

  const handleEditMemberSubmit = (e) => {
    e.preventDefault();
    if (!editingMember) return;
    if (!editMemberForm.name.trim()) {
      setEditMemberError('이름을 입력해주세요.');
      return;
    }
    const hasChanges =
      editMemberForm.name.trim() !== getMemberName(editingMember) ||
      editMemberForm.department.trim() !== String(editingMember?.department || '').trim();
    if (!hasChanges) {
      closeEditMemberModal();
      return;
    }
    setEditConfirmOpen(true);
  };

  const saveEditMember = async () => {
    if (!editingMember) return;
    const memberId = getMemberId(editingMember);
    setEditMemberLoading(true);
    setEditConfirmOpen(false);
    setEditMemberError('');
    try {
      const accountData = await updateTeamMemberProfile(memberId, {
        name: editMemberForm.name.trim(),
        department: editMemberForm.department.trim(),
      });
      if (accountData.success === false) {
        setEditMemberError(accountData.error || '계정 수정에 실패했습니다.');
        return;
      }

      closeEditMemberModal();
      await loadMembers();
    } catch (err) {
      console.error('계정 수정 실패:', err);
      setEditMemberError(err.message || '계정 수정에 실패했습니다.');
    } finally {
      setEditMemberLoading(false);
    }
  };

  const handleResetMemberPassword = async () => {
    if (!editingMember) return;
    const memberId = getMemberId(editingMember);
    setResetMemberLoading(true);
    setResetMemberError('');
    setResetPasswordResult(null);
    try {
      const data = await resetTeamMemberPassword(memberId);
      if (data.success === false) {
        setResetMemberError(data.error || '비밀번호 초기화에 실패했습니다.');
        return;
      }
      setResetPasswordResult({
        email: data.user?.email || getMemberEmail(editingMember),
        password: data.temporary_password || '0000',
      });
    } catch (err) {
      console.error('비밀번호 초기화 실패:', err);
      setResetMemberError(err.message || '비밀번호 초기화에 실패했습니다.');
    } finally {
      setResetMemberLoading(false);
    }
  };

  const openDeleteMemberModal = (member) => {
    setDeletingMember(member);
    setDeleteMemberError('');
  };

  const closeDeleteMemberModal = () => {
    if (deleteMemberLoading) return;
    setDeletingMember(null);
    setDeleteMemberError('');
  };

  const handleDeleteMember = async () => {
    if (!deletingMember) return;
    const memberId = getMemberId(deletingMember);
    setDeleteMemberLoading(true);
    setDeleteMemberError('');
    try {
      const data = await removeTeamMember(memberId);
      if (data.success === false) {
        setDeleteMemberError(data.error || '계정 삭제에 실패했습니다.');
        return;
      }
      closeDeleteMemberModal();
      await loadMembers();
    } catch (err) {
      console.error('계정 삭제 실패:', err);
      setDeleteMemberError(err.message || '계정 삭제에 실패했습니다.');
    } finally {
      setDeleteMemberLoading(false);
    }
  };

  const handleIndividualDeleteAccount = async () => {
    setDeleteAccountLoading(true);
    try {
      await deleteAccount();
      logoutUser();
      disableB2BMode();
      navigate('/individual/login');
    } catch (err) {
      console.error('계정 삭제 실패:', err);
      alert(err.message || '계정 삭제에 실패했습니다. 다시 시도해 주세요.');
      setDeleteAccountLoading(false);
      setShowDeleteAccountConfirm(false);
    }
  };

  const renderIndividualAccountSettings = () => (
    <div className="b2b-account-settings">
      <div className="b2b-settings-heading">
        <h2>계정 설정</h2>
        <p>회원님의 기본 계정 정보를 확인할 수 있습니다.</p>
      </div>

      <div className="b2b-account-card">
        <h3>기본 정보</h3>
        <div className="b2b-account-grid b2b-account-grid--single">
          <div className="b2b-form-field">
            <label htmlFor="b2b-individual-account-name">이름</label>
            <input
              id="b2b-individual-account-name"
              value={user?.name || ''}
              readOnly
              placeholder="이름"
            />
          </div>
        </div>
        <div className="b2b-form-field">
          <label htmlFor="b2b-individual-account-email">ID (이메일)</label>
          <input
            id="b2b-individual-account-email"
            value={user?.email || ''}
            readOnly
            placeholder="이메일 주소"
          />
        </div>
      </div>

      <div className="b2b-password-card b2b-account-delete-card">
        <div className="b2b-account-delete-card__copy">
          <h3>계정 삭제</h3>
          <p>계정을 삭제하면 퍼소나, 테스트, 리서치 등 모든 데이터가 영구적으로 삭제되며 복구할 수 없습니다.</p>
        </div>
        <button
          type="button"
          className="b2b-account-delete-card__btn"
          onClick={() => setShowDeleteAccountConfirm(true)}
        >
          계정 삭제
        </button>
      </div>
    </div>
  );

  const renderEnterpriseAccountSettings = () => (
    <div className="b2b-account-settings">
      <div className="b2b-settings-heading">
        <h2>계정 설정</h2>
        <p>회원님의 기본 정보와 비밀번호를 관리할 수 있습니다.</p>
      </div>

      <form className="b2b-account-card" onSubmit={handleProfileSave}>
        <h3>기본 정보 설정</h3>
        <div className="b2b-account-grid">
          <div className="b2b-form-field">
            <label htmlFor="b2b-account-name">이름</label>
            <input
              id="b2b-account-name"
              value={profileForm.name}
              onChange={(e) => handleProfileChange('name', e.target.value)}
              placeholder="이름 입력"
            />
          </div>
          <div className="b2b-form-field">
            <label htmlFor="b2b-account-department">소속 부서</label>
            <input
              id="b2b-account-department"
              value={profileForm.department}
              onChange={(e) => handleProfileChange('department', e.target.value)}
              placeholder="소속 부서 입력"
            />
          </div>
        </div>
        <div className="b2b-form-field">
          <label htmlFor="b2b-account-email">ID (이메일)</label>
          <input
            id="b2b-account-email"
            value={user?.email || ''}
            readOnly
            placeholder="이메일 주소"
          />
        </div>
        <div className="b2b-account-actions">
          {profileMessage && <span className="b2b-profile-message">{profileMessage}</span>}
          <button type="submit" disabled={!hasProfileChanges || profileLoading}>
            {profileLoading ? '저장 중...' : '변경사항 저장'}
          </button>
        </div>
      </form>

      <div className="b2b-password-card">
        <h3>비밀번호 변경</h3>
        <button type="button" onClick={() => setPasswordModalOpen(true)}>변경하기</button>
      </div>

      <p className="b2b-account-delete-note">계정 삭제가 필요하신 경우 조직 내 관리자에게 문의해 주세요.</p>
    </div>
  );

  const renderAccountSettings = () => (
    isEnterprise ? renderEnterpriseAccountSettings() : renderIndividualAccountSettings()
  );

  const renderMembersSettings = () => (
    <div className="b2b-members-settings">
      <div className="b2b-settings-heading b2b-members-heading">
        <div>
          <h2>멤버 관리</h2>
          <p>{company?.name || user?.company_name || '회사'} 멤버의 계정과 이용 상태를 관리할 수 있습니다</p>
        </div>
        <button
          type="button"
          className="b2b-member-add-button"
          onClick={() => setAddMemberModalOpen(true)}
          disabled={!isOwner || membersLoading}
          title={isOwner ? '계정 추가' : '소유자만 사용할 수 있습니다'}
        >
          계정 추가
        </button>
      </div>

      <div className="b2b-members-toolbar">
        <input
          type="search"
          value={memberSearch}
          onChange={(e) => setMemberSearch(e.target.value)}
          placeholder="이름 또는 이메일 검색"
        />
        <select value={memberSort} onChange={(e) => setMemberSort(e.target.value)}>
          <option value="created_desc">생성 최신 순</option>
          <option value="created_asc">생성 오래된 순</option>
          <option value="name_asc">이름 순</option>
        </select>
      </div>

      {membersLoading ? (
        <div className="b2b-placeholder">멤버 정보를 불러오는 중...</div>
      ) : membersError ? (
        <div className="b2b-members-error">{membersError}</div>
      ) : (
        <div className="b2b-members-table-wrap">
          <table className="b2b-members-table">
            <thead>
              <tr>
                <th>이메일</th>
                <th>이름</th>
                <th>소속부서</th>
                <th>생성일시</th>
                <th>설정</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="b2b-members-empty">
                    표시할 멤버가 없습니다.
                  </td>
                </tr>
              ) : (
                filteredMembers.map((member) => {
                  const memberId = getMemberId(member);
                  const isSelf = isCurrentUserMember(member);
                  const canEditMember = isOwner && !isSelf;
                  const canDeleteMember = isOwner && !isSelf;
                  const editButtonTitle = isSelf
                    ? '본인 정보는 계정 설정에서 수정할 수 있습니다'
                    : canEditMember
                      ? '계정 수정'
                      : '수정할 수 없습니다';

                  return (
                    <tr key={memberId || getMemberEmail(member)}>
                      <td>{getMemberEmail(member) || '-'}</td>
                      <td>
                        {getMemberName(member) || '-'}
                        {isSelf && <span className="b2b-member-self-label">(나)</span>}
                      </td>
                      <td>{getMemberDepartment(member)}</td>
                      <td>{formatMemberDate(getMemberCreatedAt(member))}</td>
                      <td>
                        <div className="b2b-member-row-actions">
                          <button
                            type="button"
                            onClick={() => openEditMemberModal(member)}
                            disabled={!canEditMember}
                            title={editButtonTitle}
                          >
                            수정
                          </button>
                          <button
                            type="button"
                            className="danger"
                            onClick={() => openDeleteMemberModal(member)}
                            disabled={!canDeleteMember}
                            title={canDeleteMember ? '계정 삭제' : '삭제할 수 없습니다'}
                          >
                            삭제
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {!isOwner && !membersLoading && (
        <p className="b2b-members-note">* 계정 추가, 수정, 삭제는 소속 owner 계정만 사용할 수 있습니다.</p>
      )}
    </div>
  );

  const renderMembershipSettings = () => {
    const tokenBalance = membershipUsage?.token_balance || membershipUsage?.balance || {};
    const byPeriod = membershipUsage?.by_period || membershipUsage?.usage?.by_period || [];
    const byUser = membershipUsage?.by_user || membershipUsage?.members || [];
    const planName =
      membershipUsage?.plan?.name ||
      membershipUsage?.plan_name ||
      company?.plan_name ||
      company?.plan_code ||
      user?.plan_name ||
      user?.plan_code ||
      'Enterprise Plan';
    const grantedTokens = Number(
      membershipUsage?.granted_weighted_tokens ??
      tokenBalance.granted_weighted_tokens ??
      0
    );
    const usedTokens = Number(membershipUsage?.used_weighted_tokens ?? tokenBalance.used_weighted_tokens ?? 0);
    const remainingTokens = Number(
      membershipUsage?.remaining_weighted_tokens ??
      tokenBalance.remaining_weighted_tokens ??
      Math.max(grantedTokens - usedTokens, 0)
    );
    const progressPercent = grantedTokens > 0
      ? Math.min(Math.round((remainingTokens / grantedTokens) * 100), 100)
      : 0;
    const maxPeriodTokens = Math.max(
      1,
      ...byPeriod.map((item) => Number(item.billable_weighted_tokens || 0))
    );
    const tableRows = byUser.length > 0
      ? byUser
      : members.map((member) => {
          const llmUsage = getMemberLlmUsage(member);
          return {
            user_id: getMemberId(member),
            email: getMemberEmail(member),
            name: getMemberName(member),
            department: getMemberDepartment(member),
            billable_weighted_tokens: llmUsage.weightedTokens,
            last_used_at: llmUsage.lastUsedAt,
          };
        });

    return (
      <div className="b2b-membership-settings">
        <div className="b2b-settings-heading">
          <h2>멤버십 관리</h2>
          <p>조직 전체 토큰 할당량 및 이용현황을 확인하고 관리할 수 있습니다.</p>
        </div>

        {membershipError && (
          <div className="b2b-membership-api-note">
            {membershipError}
          </div>
        )}

        <div className="b2b-membership-summary-grid">
          <section className="b2b-membership-summary-card">
            <div>
              <span>현재 멤버십 플랜</span>
              <strong>{planName}</strong>
            </div>
          </section>

          <section className="b2b-membership-summary-card">
            <div className="b2b-membership-token-head">
              <div>
                <span>현재 잔여 토큰</span>
                <strong>{membershipLoading ? '...' : formatTokenCount(remainingTokens)}</strong>
              </div>
            </div>
            <div className="b2b-membership-progress">
              <div style={{ width: `${progressPercent}%` }} />
            </div>
            <div className="b2b-membership-progress-meta">
              <span>잔여 {formatTokenCount(remainingTokens)} Tokens</span>
              <span>총 {formatTokenCount(grantedTokens)} Tokens</span>
            </div>
          </section>
        </div>

        <section className="b2b-membership-chart-card">
          <div className="b2b-membership-section-head">
            <h3>토큰 이용 추이</h3>
            <div className="b2b-membership-filters">
              <select
                value={membershipRange}
                onChange={(e) => setMembershipRange(e.target.value)}
                disabled={membershipLoading}
              >
                <option value="30">최근 1개월</option>
                <option value="90">최근 3개월</option>
                <option value="180">최근 6개월</option>
              </select>
            </div>
          </div>
          {membershipLoading ? (
            <div className="b2b-membership-empty">사용량을 불러오는 중...</div>
          ) : byPeriod.length === 0 ? (
            <div className="b2b-membership-empty">표시할 토큰 이용 추이 데이터가 없습니다.</div>
          ) : (
            <div className="b2b-membership-bars" aria-label="토큰 이용 추이 막대 차트">
              {byPeriod.map((item) => {
                const value = Number(item.billable_weighted_tokens || 0);
                const height = Math.max(4, Math.round((value / maxPeriodTokens) * 154));
                return (
                  <div
                    className="b2b-membership-bar-item"
                    key={item.period}
                    style={{ '--bar-height': `${height}px` }}
                  >
                    <div className="b2b-membership-bar" />
                    <div className="b2b-membership-chart-tooltip">
                      <strong>{formatChartTooltipDate(item.period)}</strong>
                      <div>
                        <span className="b2b-membership-tooltip-swatch" />
                        <span>Tokens</span>
                        <b>{formatTokenCount(value)}</b>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="b2b-membership-axis">
                <span>{byPeriod[0]?.period || ''}</span>
                <span>{byPeriod[Math.floor(byPeriod.length / 2)]?.period || ''}</span>
                <span>{byPeriod[byPeriod.length - 1]?.period || ''}</span>
              </div>
            </div>
          )}
        </section>

        <section className="b2b-membership-table-section">
          <h3>멤버별 토큰 소모 내역</h3>
          <div className="b2b-members-table-wrap">
            <table className="b2b-membership-table">
              <thead>
                <tr>
                  <th>이메일</th>
                  <th>이름</th>
                  <th>소속부서</th>
                  <th>소모 토큰</th>
                  <th>마지막 사용일</th>
                </tr>
              </thead>
              <tbody>
                {membershipLoading ? (
                  <tr>
                    <td colSpan={5} className="b2b-members-empty">멤버별 사용량을 불러오는 중...</td>
                  </tr>
                ) : tableRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="b2b-members-empty">표시할 멤버별 사용량이 없습니다.</td>
                  </tr>
                ) : (
                  tableRows.map((row) => (
                    <tr key={row.user_id || row.email}>
                      <td>{row.email || '-'}</td>
                      <td>{row.name || '-'}</td>
                      <td>{row.department || '-'}</td>
                      <td>{formatTokenCount(row.billable_weighted_tokens)} Tokens</td>
                      <td>{formatMemberDate(row.last_used_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    );
  };

  const renderPlaceholderSettings = () => (
    <>
      <h2>{sectionLabel}</h2>
      <div className="b2b-placeholder">준비 중입니다...</div>
    </>
  );

  return (
    <B2BLayout hideCollapseButton={false} defaultCollapsed={true} hidePageHeader={true}>
      <div className="b2b-settings-page">
        <div className="b2b-page-content b2b-settings-content">
          {section === 'account' ? (
            renderAccountSettings()
          ) : section === 'members' ? (
            teamLoaded && isOwner ? renderMembersSettings() : (
              <div className="b2b-placeholder">권한을 확인하는 중...</div>
            )
          ) : section === 'membership' ? (
            teamLoaded && isOwner ? renderMembershipSettings() : (
              <div className="b2b-placeholder">권한을 확인하는 중...</div>
            )
          ) : (
            renderPlaceholderSettings()
          )}
        </div>
      </div>

      {passwordModalOpen && (
        <div className="b2b-password-modal-overlay b2b-password-change-modal-overlay">
          <div
            className="b2b-password-modal b2b-password-change-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="b2b-password-change-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="b2b-password-change-modal__header">
              <h2 id="b2b-password-change-title">비밀번호 변경</h2>
            </header>
            <form className="b2b-password-change-modal__form" onSubmit={handlePasswordSubmit}>
              <div className={`b2b-form-field ${passwordFieldErrors.currentPassword ? 'has-error' : ''}`}>
                <label htmlFor="b2b-current-password">현재 비밀번호</label>
                <input
                  id="b2b-current-password"
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => handlePasswordFormChange('currentPassword', e.target.value)}
                  placeholder="현재 비밀번호 입력"
                  disabled={passwordLoading}
                  aria-invalid={Boolean(passwordFieldErrors.currentPassword)}
                  aria-describedby={passwordFieldErrors.currentPassword ? 'b2b-current-password-error' : undefined}
                />
                {passwordFieldErrors.currentPassword ? (
                  <p id="b2b-current-password-error" className="b2b-password-helper-error">
                    {passwordFieldErrors.currentPassword}
                  </p>
                ) : null}
              </div>
              <div className={`b2b-form-field ${passwordFieldErrors.newPassword ? 'has-error' : ''}`}>
                <label htmlFor="b2b-new-password">새 비밀번호</label>
                <input
                  id="b2b-new-password"
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => handlePasswordFormChange('newPassword', e.target.value)}
                  placeholder="새 비밀번호 입력"
                  disabled={passwordLoading}
                  aria-invalid={Boolean(passwordFieldErrors.newPassword)}
                  aria-describedby={passwordFieldErrors.newPassword ? 'b2b-new-password-error' : undefined}
                />
                {passwordFieldErrors.newPassword ? (
                  <p id="b2b-new-password-error" className="b2b-password-helper-error">
                    {passwordFieldErrors.newPassword}
                  </p>
                ) : null}
              </div>
              <div className={`b2b-form-field ${passwordFieldErrors.confirmPassword ? 'has-error' : ''}`}>
                <label htmlFor="b2b-confirm-password">비밀번호 확인</label>
                <input
                  id="b2b-confirm-password"
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => handlePasswordFormChange('confirmPassword', e.target.value)}
                  placeholder="새 비밀번호 한번 더 입력"
                  disabled={passwordLoading}
                  aria-invalid={Boolean(passwordFieldErrors.confirmPassword)}
                  aria-describedby={passwordFieldErrors.confirmPassword ? 'b2b-confirm-password-error' : undefined}
                />
                {passwordFieldErrors.confirmPassword ? (
                  <p id="b2b-confirm-password-error" className="b2b-password-helper-error">
                    {passwordFieldErrors.confirmPassword}
                  </p>
                ) : null}
              </div>
              <div className="b2b-password-modal-actions">
                <button type="button" onClick={resetPasswordModal} disabled={passwordLoading}>취소하기</button>
                <button type="submit" disabled={!isPasswordFormComplete || passwordLoading}>{passwordLoading ? '변경 중...' : '변경하기'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {addMemberModalOpen && (
        <div className="b2b-password-modal-overlay" onClick={closeAddMemberModal}>
          <div className="b2b-password-modal b2b-member-modal b2b-member-add-modal" onClick={(e) => e.stopPropagation()}>
            <h2>사용자 계정 추가</h2>
            <form onSubmit={handleAddMember}>
              <div className={`b2b-form-field ${addMemberFieldErrors.email ? 'has-error' : ''}`}>
                <label htmlFor="b2b-member-email">ID (이메일)</label>
                <input
                  id="b2b-member-email"
                  type="text"
                  value={addMemberForm.email}
                  onChange={(e) => handleAddMemberFormChange('email', e.target.value)}
                  placeholder="회사 이메일 주소 입력"
                  disabled={addMemberLoading}
                  aria-invalid={Boolean(addMemberFieldErrors.email)}
                  aria-describedby={addMemberFieldErrors.email ? 'b2b-member-email-error' : undefined}
                />
                {addMemberFieldErrors.email ? (
                  <p id="b2b-member-email-error" className="b2b-password-helper-error">
                    {addMemberFieldErrors.email}
                  </p>
                ) : null}
              </div>
              <div className="b2b-member-add-grid">
                <div className={`b2b-form-field ${addMemberFieldErrors.name ? 'has-error' : ''}`}>
                  <label htmlFor="b2b-member-name">이름</label>
                  <input
                    id="b2b-member-name"
                    value={addMemberForm.name}
                    onChange={(e) => handleAddMemberFormChange('name', e.target.value)}
                    placeholder="이름 입력"
                    disabled={addMemberLoading}
                    aria-invalid={Boolean(addMemberFieldErrors.name)}
                    aria-describedby={addMemberFieldErrors.name ? 'b2b-member-name-error' : undefined}
                  />
                  {addMemberFieldErrors.name ? (
                    <p id="b2b-member-name-error" className="b2b-password-helper-error">
                      {addMemberFieldErrors.name}
                    </p>
                  ) : null}
                </div>
                <div className={`b2b-form-field ${addMemberFieldErrors.department ? 'has-error' : ''}`}>
                  <label htmlFor="b2b-member-department">소속 부서</label>
                  <input
                    id="b2b-member-department"
                    value={addMemberForm.department}
                    onChange={(e) => handleAddMemberFormChange('department', e.target.value)}
                    placeholder="소속 부서명 입력"
                    disabled={addMemberLoading}
                    aria-invalid={Boolean(addMemberFieldErrors.department)}
                    aria-describedby={addMemberFieldErrors.department ? 'b2b-member-department-error' : undefined}
                  />
                  {addMemberFieldErrors.department ? (
                    <p id="b2b-member-department-error" className="b2b-password-helper-error">
                      {addMemberFieldErrors.department}
                    </p>
                  ) : null}
                </div>
              </div>
              <ul className="b2b-member-add-hints">
                <li>이메일은 회사에서 사용하는 이메일 주소를 작성해주세요.</li>
                <li>계정 생성 후 발급되는 비밀번호는 임시 비밀번호입니다.</li>
                <li>최초 로그인 후 비밀번호를 변경해주세요.</li>
              </ul>
              <div className="b2b-password-modal-actions">
                <button type="submit" disabled={!isAddMemberFormComplete || addMemberLoading}>
                  {addMemberLoading ? '추가 중...' : '추가하기'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {createdMemberResult && (
        <div className="b2b-password-modal-overlay">
          <div className="b2b-created-member-modal" onClick={(e) => e.stopPropagation()}>
            <h2>계정이 생성되었습니다.</h2>
            <ul>
              <li>이메일: {createdMemberResult.email}</li>
              {createdMemberResult.password && (
                <li>임시비밀번호: {createdMemberResult.password}</li>
              )}
            </ul>
            <button type="button" onClick={() => setCreatedMemberResult(null)}>확인</button>
          </div>
        </div>
      )}

      {editingMember && (
        <div className="b2b-password-modal-overlay">
          <div className="b2b-password-modal b2b-member-modal b2b-member-edit-modal" onClick={(e) => e.stopPropagation()}>
            <h2>사용자 계정 수정</h2>
            <form onSubmit={handleEditMemberSubmit}>
              {editMemberError && <div className="b2b-password-error">{editMemberError}</div>}
              <div className="b2b-form-field">
                <label htmlFor="b2b-edit-member-email">ID (이메일)</label>
                <input
                  id="b2b-edit-member-email"
                  value={getMemberEmail(editingMember) || ''}
                  readOnly
                />
              </div>
              <div className="b2b-member-edit-grid">
                <div className="b2b-form-field">
                  <label htmlFor="b2b-edit-member-name">이름</label>
                  <input
                    id="b2b-edit-member-name"
                    value={editMemberForm.name}
                    onChange={(e) => handleEditMemberFormChange('name', e.target.value)}
                    disabled={editMemberLoading}
                  />
                </div>
                <div className="b2b-form-field">
                  <label htmlFor="b2b-edit-member-department">소속 부서</label>
                  <input
                    id="b2b-edit-member-department"
                    value={editMemberForm.department}
                    onChange={(e) => handleEditMemberFormChange('department', e.target.value)}
                    disabled={editMemberLoading}
                    placeholder="소속 부서 입력"
                  />
                </div>
              </div>
              <ul className="b2b-member-edit-hints">
                <li>이메일은 회사에서 사용하는 이메일 주소를 확인해주세요.</li>
                <li>계정 생성 후 발급되는 비밀번호는 임시 비밀번호입니다.</li>
                <li>최초 로그인 후 비밀번호를 변경해주세요.</li>
              </ul>
              <div className="b2b-member-password-reset">
                <label>비밀번호 변경</label>
                <button
                  type="button"
                  onClick={handleResetMemberPassword}
                  disabled={resetMemberLoading}
                >
                  {resetMemberLoading ? '초기화 중...' : '비밀번호 초기화'}
                </button>
                <p>비밀번호 초기화 시 기존 비밀번호는 삭제됩니다.</p>
                {resetMemberError && <div className="b2b-password-error">{resetMemberError}</div>}
              </div>
              <div className="b2b-password-modal-actions">
                <button type="button" onClick={closeEditMemberModal} disabled={editMemberLoading}>취소하기</button>
                <button type="submit" disabled={editMemberLoading}>{editMemberLoading ? '수정 중...' : '수정하기'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetPasswordResult && (
        <div className="b2b-password-modal-overlay">
          <div className="b2b-issued-password-modal" onClick={(e) => e.stopPropagation()}>
            <h2>임시 비밀번호가<br />발급되었습니다.</h2>
            <ul>
              <li>이메일: {resetPasswordResult.email}</li>
              <li>임시비밀번호: {resetPasswordResult.password}</li>
            </ul>
            <button type="button" onClick={() => setResetPasswordResult(null)}>확인</button>
          </div>
        </div>
      )}

      {editConfirmOpen && (
        <div className="b2b-confirm-modal-overlay">
          <div className="b2b-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h2>변경사항을 저장하시겠습니까?</h2>
            <p>변경된 정보가 저장됩니다.</p>
            <div className="b2b-confirm-modal-actions">
              <button type="button" onClick={() => setEditConfirmOpen(false)} disabled={editMemberLoading}>
                취소
              </button>
              <button type="button" onClick={saveEditMember} disabled={editMemberLoading}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {deletingMember && (
        <div className="b2b-password-modal-overlay">
          <div className="b2b-password-modal b2b-member-modal" onClick={(e) => e.stopPropagation()}>
            <h2>해당 계정을 삭제하시겠습니까?</h2>
            <div className="b2b-member-delete-copy">
              <p>계정 삭제 시, 해당 멤버가 생성한 모든 리서치 결과물도<br /> 함께 삭제되며 복구할 수 없습니다.</p>
            </div>
            {deleteMemberError && <div className="b2b-password-error">{deleteMemberError}</div>}
            <div className="b2b-password-modal-actions">
              <button type="button" onClick={closeDeleteMemberModal} disabled={deleteMemberLoading}>취소</button>
              <button type="button" onClick={handleDeleteMember} disabled={deleteMemberLoading}>
                {deleteMemberLoading ? '삭제 중...' : '계정 삭제'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={showDeleteAccountConfirm}
        type="error"
        className="account-delete-confirm-modal"
        title="계정 삭제 확인"
        message="계정을 삭제하면 퍼소나, 테스트, 리서치 등 모든 데이터가 영구적으로 삭제됩니다."
        subMessage="이 작업은 되돌릴 수 없습니다. 정말 삭제하시겠습니까?"
        confirmText={deleteAccountLoading ? '삭제 중...' : '삭제'}
        cancelText="취소"
        showCancel={true}
        confirmDisabled={deleteAccountLoading}
        onConfirm={handleIndividualDeleteAccount}
        onCancel={() => !deleteAccountLoading && setShowDeleteAccountConfirm(false)}
        canCloseOutside={false}
      />
      <Toast
        isOpen={toast.isOpen}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((current) => ({ ...current, isOpen: false }))}
      />
    </B2BLayout>
  );
};

export default B2BSettingsPage;
